import { useState } from "react";
import toast from "react-hot-toast";
import UserProfileCard from "../../components/UserProfileCard";
import { getAllProjects } from "../../api/Projects";
import { submitHelpQuery } from "../../api/Queries";
import { useAuth } from "../../contexts/AuthContext";
import { Project } from "../../utils/interface/project";
import { extractApiError } from "../../utils/util";
import messageImage from "../../assets/massage.svg";
import SelectField from "../../components/Projects/SelectField";
import TextAreaField from "../../components/Projects/TextAreaField";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryKeys } from "../../utils/queryKeys";

const QUERY_MIN_LENGTH = 10;
const QUERY_MAX_LENGTH = 2000;

const Help = () => {
  const { userDetails } = useAuth();
  const [selectedProject, setSelectedProject] = useState("");
  const [query, setQuery] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [touched, setTouched] = useState<{
    project?: boolean;
    query?: boolean;
  }>({});
  const [errors, setErrors] = useState<{ project?: string; query?: string }>(
    {},
  );

  const { data: projectsData, isLoading: projectsLoading } = useQuery<{
    projects?: Project[];
  }>({
    queryKey: queryKeys.projects.helpSelect(),
    queryFn: async () => getAllProjects(1, 50),
  });
  const projects = projectsData?.projects ?? [];

  const submitMutation = useMutation({
    mutationFn: submitHelpQuery,
  });

  const projectOptions = [
    { value: "", label: "Select a project" },
    ...projects.map((p) => ({ value: p.id, label: p.name })),
  ];

  const handleChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    if (name === "project") setSelectedProject(value);
    if (name === "query") setQuery(value);
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const newErrors: { project?: string; query?: string } = {};
    if (!selectedProject) newErrors.project = "Please select a project";
    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      newErrors.query = "Please enter your query";
    } else if (trimmedQuery.length < QUERY_MIN_LENGTH) {
      newErrors.query = `Query must be at least ${QUERY_MIN_LENGTH} characters (currently ${trimmedQuery.length})`;
    } else if (query.length > QUERY_MAX_LENGTH) {
      newErrors.query = `Query must be at most ${QUERY_MAX_LENGTH} characters (currently ${query.length})`;
    }
    if (!userDetails?.id && !userDetails?.userId) {
      setSubmitError("Please sign in again.");
      return;
    }
    setErrors(newErrors);
    setTouched({ project: true, query: true });
    if (Object.keys(newErrors).length > 0) return;

    try {
      await submitMutation.mutateAsync({
        projectId: selectedProject,
        queries: query.trim(),
      });
      setQuery("");
      setSelectedProject("");
      setTouched({});
      setErrors({});
      toast.success("Your query has been submitted successfully.");
    } catch (error) {
      const message = extractApiError(error);
      setSubmitError(message);
      toast.error(message);
    }
  };

  if (projectsLoading) {
    return (
      <div className="space-y-6">
        <UserProfileCard />
        <div className="heading-wrap">
          <h1 className="text-[34px] lg:text-[38px] xl:text-[56px] font-display font-weight-300 text-white mb-2">
            Help
          </h1>
          <p className="text-gray-300 font-weight-300 text-[14px] lg:text-[16px] xl:text-[18px]">
            If you need to ask us a question or raise an issue you have had with
            your IFC uploads please send us the details below making sure to
            mention who you are and selecting which project you are referencing.
          </p>
        </div>
        <div className="flex items-center justify-center min-h-[280px]">
          <div
            className="w-10 h-10 border-2 border-[#E761A0] border-t-transparent rounded-full animate-spin"
            aria-label="Loading projects"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <UserProfileCard />
      <div className="heading-wrap  ">
        <h1 className="text-[34px] lg:text-[38px] xl:text-[56px] font-display font-weight-300 text-white mb-2">
          Help
        </h1>
        <p className="text-gray-300 font-weight-300  text-[14px] lg:text-[16px] xl:text-[18px ">
          If you need to ask us a question or raise an issue you have had with
          your IFC uploads please send us the details below making sure to
          mention who you are and selecting which project you are referencing.
        </p>
      </div>

      <div className="flex  flex-col-reverse md:flex-row gap-8 items-start">
        {/* Form section */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 w-full md:max-w-xl space-y-5 help-form"
        >
          <SelectField
            label="Project"
            name="project"
            value={selectedProject}
            onChange={
              handleChange as (e: React.ChangeEvent<HTMLSelectElement>) => void
            }
            onBlur={
              handleBlur as (e: React.FocusEvent<HTMLSelectElement>) => void
            }
            options={projectOptions}
            required
            touched={!!touched.project}
            error={errors.project}
          />
          <TextAreaField
            label="Query"
            name="query"
            value={query}
            onChange={
              handleChange as (
                e: React.ChangeEvent<HTMLTextAreaElement>,
              ) => void
            }
            onBlur={
              handleBlur as (e: React.FocusEvent<HTMLTextAreaElement>) => void
            }
            placeholder="Type your query here..."
            rows={5}
            required
            minLength={QUERY_MIN_LENGTH}
            maxLength={QUERY_MAX_LENGTH}
            touched={!!touched.query}
            error={errors.query}
          />

          {submitError && (
            <p className="text-red-500 text-sm" role="alert">
              {submitError}
            </p>
          )}

          <div className="flex justify-start pt-2">
            <button
              type="submit"
              disabled={submitMutation.isPending || !userDetails}
              className="theme-btn w-[136px] text-uppercase"
            >
              {submitMutation.isPending ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>

        {/* Message / help graphic from assets */}
        <div className="w-full  flex flex-1 md:justify-start justify-center items-center md:min-h-[280px] min-h-auto lg:pl-10 pl-0 pb-[25px] lg:text-center text-left ">
          <img
            src={messageImage || undefined}
            alt=""
            className="max-w-[200px] w-full h-auto object-contain lg:ml-0 mx-auto flex"
            aria-hidden
          />
        </div>
      </div>
    </div>
  );
};

export default Help;
